// A map (docs/spec/maps.md): MapLibre drawing our Protomaps basemap (OpenStreetMap data) from our own
// server, in the light or dark style. MapLibre and PMTiles load only when a map is first shown, so
// screens without one carry none of it. Markers: stops (numbered), the school, the bus.
import { useEffect, useRef, useState } from "react";
import { apiBase } from "../api.ts";
import "./map.css";

export interface MapMarker {
  id: string;
  lat: number;
  lng: number;
  kind: "stop" | "school" | "bus" | "me";
  /** Short text on the marker (a stop's number) and its accessible name. */
  label?: string;
  title: string;
  /** Called with the new position when the marker is dragged (editing a stop). */
  onMove?: (lat: number, lng: number) => void;
  /** Clicking the marker (choosing a bus on the dispatcher's map). */
  onClick?: () => void;
  /** A state shown by colour: a late or stale bus, the selected one; a stop done or next. */
  tone?: "late" | "stale" | "alert" | "selected" | "muted" | "done" | "next";
  /** A bus's heading, degrees from north: an arrow on the marker points that way. */
  heading?: number | null;
}

/** A route drawn on the map ([lng, lat] points), in its own colour. */
export interface MapLine { id: string; coords: [number, number][]; color: string; width?: number; opacity?: number; /** Dashed: the way still to go. */ dash?: boolean }

export interface MapViewProps {
  markers: MapMarker[];
  /** Clicking the map (placing a stop). */
  onPick?: (lat: number, lng: number) => void;
  /** With no markers: where to look. */
  center?: { lat: number; lng: number };
  /** Keep the view on these marker ids whenever they move (the bus). */
  follow?: string[];
  /** Fit the first view to these marker ids only (the buses on the road), not every marker. */
  fitTo?: string[];
  height?: number | string;
  label: string;
  /** A route to draw, as [lng, lat] points (the driver's directions). */
  line?: [number, number][] | null;
  /** Several routes at once, each in its colour (the dispatcher's maps). */
  lines?: MapLine[];
  /**
   * Driving: the view follows this point, turned to the heading, instead of fitting the markers.
   * `padding` moves the point off centre (low on the screen, below the directions); a new `seq`
   * places the view again at the same point (the driver asked to re-centre).
   */
  camera?: { lng: number; lat: number; bearing: number; zoom?: number; pitch?: number; padding?: { top: number; bottom: number }; seq?: number } | null;
  /** Called when the person pans, zooms or turns the map by hand (the drive screen stops following). */
  onUserMove?: () => void;
  /** Pixels covered by something laid over the map (a panel at the bottom): fitting and following keep clear of them. */
  inset?: { top?: number; bottom?: number };
  /** Fit the view to these [lng, lat] points, flat and north-up; again whenever `key` changes. */
  overview?: { key: string; points: [number, number][]; padding?: { top: number; bottom: number; left: number; right: number } } | null;
}

// Map symbols, filled so they read at marker size (24-unit grid, currentColor).
// The driver's own position: a navigation arrow, turned to the heading.
const ME_SVG = `<svg viewBox="0 0 48 48" aria-hidden="true" class="pmu-map-pin__icon"><circle cx="24" cy="24" r="22" fill="#fff" opacity=".96"/><circle cx="24" cy="24" r="18" fill="currentColor"/><path d="M24 10 34 34 24 28.5 14 34Z" fill="#fff" stroke="#fff" stroke-width="1.5" stroke-linejoin="round"/></svg>`;
const BUS_SVG = `<svg viewBox="0 0 24 24" aria-hidden="true" class="pmu-map-pin__icon"><path fill="currentColor" d="M6.5 3h11A3 3 0 0 1 20.5 6v10a2 2 0 0 1-1 1.73V20a1 1 0 0 1-1 1H17a1 1 0 0 1-1-1v-1.5H8V20a1 1 0 0 1-1 1H5.5a1 1 0 0 1-1-1v-2.27a2 2 0 0 1-1-1.73V6a3 3 0 0 1 3-3Z"/><rect x="5.5" y="5.5" width="13" height="6" rx="1.2" fill="#fff" opacity=".92"/><circle cx="7.5" cy="14.8" r="1.3" fill="#fff"/><circle cx="16.5" cy="14.8" r="1.3" fill="#fff"/><rect x="1.4" y="6.5" width="1.6" height="3.5" rx=".8" fill="currentColor"/><rect x="21" y="6.5" width="1.6" height="3.5" rx=".8" fill="currentColor"/></svg>`;
const SCHOOL_SVG = `<svg viewBox="0 0 24 24" aria-hidden="true" class="pmu-map-pin__icon"><path fill="currentColor" d="M12 2.8 21 8v1.6H3V8z"/><path fill="currentColor" d="M4.2 10.6h15.6v9.6a.8.8 0 0 1-.8.8H5a.8.8 0 0 1-.8-.8z"/><rect x="10.3" y="15" width="3.4" height="6" rx=".5" fill="#fff"/><rect x="6.2" y="12.4" width="2.6" height="2.6" rx=".4" fill="#fff"/><rect x="15.2" y="12.4" width="2.6" height="2.6" rx=".4" fill="#fff"/><circle cx="12" cy="7.3" r="1.2" fill="#fff"/></svg>`;

type Lib = { maplibregl: typeof import("maplibre-gl"); style: (dark: boolean) => import("maplibre-gl").StyleSpecification };
let lib: Promise<Lib> | null = null;

/** Loads MapLibre, PMTiles and the style once, and registers the pmtiles:// protocol. */
function loadLib(): Promise<Lib> {
  lib ??= (async () => {
    const [maplibregl, { Protocol }, { layers, namedFlavor }, { default: workerUrl }] = await Promise.all([
      import("maplibre-gl"), import("pmtiles"), import("@protomaps/basemaps"),
      // MapLibre's worker, bundled by Vite as a file of our own origin (the CSP allows no other script).
      import("maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url"), import("maplibre-gl/dist/maplibre-gl.css"),
    ]);
    maplibregl.setWorkerUrl(workerUrl);
    const protocol = new Protocol();
    maplibregl.addProtocol("pmtiles", protocol.tile);
    const origin = apiBase() || location.origin;
    const cfg = await fetch(`${origin}/api/v1/maps/config`).then((r) => r.json() as Promise<{ pmtiles: string; glyphs: string; sprite: string; attribution: string }>);
    // Protomaps' dark flavour draws roads a shade off the ground and names in dark grey: at night,
    // on a phone in a cab, the driver saw no streets. Roads and their names here are lifted to read.
    const base = namedFlavor("dark");
    const DARK = {
      ...base, earth: "#1d1f22", background: "#2a2d33",
      highway: "#8a8f98", highway_casing_early: "#1d1f22", highway_casing_late: "#1d1f22",
      major: "#6f747c", major_casing_early: "#1d1f22", major_casing_late: "#1d1f22", link: "#6f747c",
      minor_a: "#585c63", minor_b: "#50545a", minor_service: "#45484e", other: "#45484e", minor_casing: "#1d1f22",
      bridges_major: "#6f747c", bridges_minor: "#585c63", bridges_highway: "#8a8f98",
      roads_label_major: "#e4e0d8", roads_label_minor: "#c3beb5", roads_label_major_halo: "#1d1f22", roads_label_minor_halo: "#1d1f22",
      city_label: "#e9e5dd", subplace_label: "#b9b4ab", water: "#27415c", buildings: "#2b2e33",
    };
    const style = (dark: boolean): import("maplibre-gl").StyleSpecification => ({
      version: 8,
      glyphs: `${origin}${cfg.glyphs}`,
      sprite: `${origin}${cfg.sprite}/${dark ? "dark" : "light"}`,
      sources: { protomaps: { type: "vector", url: `pmtiles://${origin}${cfg.pmtiles}`, attribution: cfg.attribution } },
      layers: layers("protomaps", dark ? DARK : namedFlavor("light"), { lang: "en" }) as import("maplibre-gl").LayerSpecification[],
    });
    return { maplibregl, style };
  })();
  return lib;
}

/** The driving view: where, which way up, how close, how tilted. */
interface Cam { lng: number; lat: number; bearing: number; zoom: number; pitch: number }
/** Where a glide is at time `t`: evenly along the way, turning the short way round. */
function camAt(g: { from: Cam; to: Cam; start: number; dur: number }, t: number): Cam {
  const k = Math.min(1, Math.max(0, (t - g.start) / g.dur));
  const turn = ((((g.to.bearing - g.from.bearing) % 360) + 540) % 360) - 180;
  const mix = (a: number, b: number) => a + (b - a) * k;
  return { lng: mix(g.from.lng, g.to.lng), lat: mix(g.from.lat, g.to.lat), bearing: (g.from.bearing + turn * k + 360) % 360, zoom: mix(g.from.zoom, g.to.zoom), pitch: mix(g.from.pitch, g.to.pitch) };
}

const isDark = () => {
  const t = document.documentElement.getAttribute("data-theme");
  return t ? t === "dark" : window.matchMedia("(prefers-color-scheme: dark)").matches;
};

export function MapView({ markers, onPick, center, follow, fitTo, height = 280, label, line, lines, camera, onUserMove, overview, inset }: MapViewProps) {
  const box = useRef<HTMLDivElement>(null);
  const map = useRef<import("maplibre-gl").Map | null>(null);
  const pins = useRef(new Map<string, import("maplibre-gl").Marker>());
  const drags = useRef(new WeakMap<import("maplibre-gl").Marker, () => void>());
  const [ready, setReady] = useState(false);
  // Bumped when the style is replaced (light/dark): the line layers go with the old style.
  const [styleGen, setStyleGen] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const pick = useRef(onPick);
  pick.current = onPick;
  const moved = useRef(onUserMove);
  moved.current = onUserMove;
  // The driving view's glide between fixes (below), and the last re-centre asked for.
  const glide = useRef<{ from: Cam; to: Cam; start: number; dur: number; last: number; frame: number } | null>(null);
  const lastSeq = useRef<number | undefined>(undefined);

  useEffect(() => {
    let gone = false;
    loadLib().then(({ maplibregl, style }) => {
      if (gone || !box.current) return;
      const first = markers[0] ?? null;
      const m = new maplibregl.Map({
        container: box.current, style: style(isDark()), attributionControl: { compact: true },
        center: first ? [first.lng, first.lat] : center ? [center.lng, center.lat] : [-98.5, 39.8], zoom: first || center ? 13 : 3,
      });
      // Driving (a camera) has its own buttons; the zoom control would sit under them.
      if (!camera) m.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
      m.on("click", (e: import("maplibre-gl").MapMouseEvent) => pick.current?.(e.lngLat.lat, e.lngLat.lng));
      // By hand only: our own easeTo carries no originalEvent.
      for (const ev of ["dragstart", "zoomstart", "rotatestart", "pitchstart"] as const) m.on(ev, (e: { originalEvent?: Event }) => { if (e.originalEvent) moved.current?.(); });
      m.on("load", () => {
        // The compact attribution starts open and covers a small map; it opens again on a tap.
        box.current?.querySelector(".maplibregl-ctrl-attrib")?.classList.remove("maplibregl-compact-show");
        setReady(true);
      });
      map.current = m;
      // For tests and support: the map behind its element.
      (box.current as HTMLDivElement & { vavrMap?: unknown }).vavrMap = m;
    }, () => setError("The map did not load."));
    // Light or dark chosen (or the device switching): the same map, restyled. Our lines are re-added once the new style loads.
    const restyle = () => { void loadLib().then(({ style }) => { const m = map.current; if (!m) return; m.setStyle(style(isDark())); m.once("style.load", () => setStyleGen((g) => g + 1)); }); };
    window.addEventListener("pmu-theme", restyle);
    return () => { gone = true; window.removeEventListener("pmu-theme", restyle); map.current?.remove(); map.current = null; pins.current.clear(); };
    // The map is made once; markers and view follow below.
  }, []);

  // Markers: added, moved or removed to match the props; the view fits them once, then follows the bus.
  const fitted = useRef(false);
  useEffect(() => {
    const m = map.current;
    if (!m || !ready) return;
    void loadLib().then(({ maplibregl }) => {
      const seen = new Set<string>();
      for (const k of markers) {
        seen.add(k.id);
        const pin = pins.current.get(k.id) ?? (() => {
          const el = document.createElement("div");
          el.className = `pmu-map-pin pmu-map-pin--${k.kind}`;
          el.setAttribute("role", "img");
          // A school is a pin standing on its place; the rest sit centred on theirs.
          // The driver's arrow turns with the map, so it keeps pointing the way the bus goes, and stands upright when tilted.
          const flat = k.kind === "me" ? { rotationAlignment: "map" as const, pitchAlignment: "viewport" as const } : {};
          const made = new maplibregl.Marker({ element: el, anchor: k.kind === "school" ? "bottom" : "center", ...flat }).setLngLat([k.lng, k.lat]).addTo(m);
          pins.current.set(k.id, made);
          return made;
        })();
        const el = pin.getElement();
        if (k.kind === "bus" || k.kind === "school") {
          // A symbol, and the bus's route tag beside it (text set safely, never as HTML).
          // The school's teardrop turns on an inner element: MapLibre places the marker itself with `transform`.
          el.innerHTML = k.kind === "bus" ? BUS_SVG : `<span class="pmu-map-pin__drop">${SCHOOL_SVG}</span>`;
          if (k.label) { const tag = document.createElement("span"); tag.className = "pmu-map-pin__tag"; tag.textContent = k.label; el.append(tag); }
          // The way the bus points: an arrow on the badge's rim (an inner element; the marker itself is placed by MapLibre).
          if (k.kind === "bus" && k.heading != null) { const h = document.createElement("span"); h.className = "pmu-map-pin__heading"; h.style.rotate = `${Math.round(k.heading)}deg`; el.prepend(h); }
        } else if (k.kind === "me") {
          if (!el.firstChild) el.innerHTML = ME_SVG;
          // While the view follows the driver, the glide moves and turns the arrow with it.
          if (!camera) pin.setRotation(k.heading ?? 0);
        } else el.textContent = k.label ?? "";
        // Only our own classes change: MapLibre's (maplibregl-marker…) position the element.
        for (const c of ["late", "stale", "alert", "selected", "muted", "done", "next", "button"]) el.classList.toggle(`pmu-map-pin--${c}`, k.tone === c || (c === "button" && !!k.onClick));
        if (k.label) el.dataset.label = ""; else delete el.dataset.label;
        el.onclick = k.onClick ? (e) => { e.stopPropagation(); k.onClick!(); } : null;
        if (k.onClick) { el.tabIndex = 0; el.onkeydown = (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); k.onClick!(); } }; } else { el.removeAttribute("tabindex"); el.onkeydown = null; }
        el.setAttribute("aria-label", k.title);
        el.title = k.title;
        if (!(k.kind === "me" && camera && glide.current)) pin.setLngLat([k.lng, k.lat]);
        pin.setDraggable(!!k.onMove);
        // The newest handler only: the marker is reused across renders.
        const before = drags.current.get(pin);
        if (before) pin.off("dragend", before);
        if (k.onMove) {
          const onMove = k.onMove;
          const handler = () => { const p = pin.getLngLat(); onMove(p.lat, p.lng); };
          drags.current.set(pin, handler);
          pin.on("dragend", handler);
        }
      }
      for (const [id, pin] of pins.current) if (!seen.has(id)) { pin.remove(); pins.current.delete(id); }
      const fitSet = fitTo?.length ? markers.filter((k) => fitTo.includes(k.id)) : markers;
      const top = inset?.top ?? 0, bottom = inset?.bottom ?? 0;
      if (!camera) m.setPadding({ top, bottom, left: 0, right: 0 });
      if (camera) { /* the camera effect below places the view */ } else if (!fitted.current && fitSet.length) {
        fitted.current = true;
        if (fitSet.length === 1) m.jumpTo({ center: [fitSet[0]!.lng, fitSet[0]!.lat], zoom: 14 });
        else {
          const b = new maplibregl.LngLatBounds();
          for (const k of fitSet) b.extend([k.lng, k.lat]);
          m.fitBounds(b, { padding: { top: 48 + top, bottom: 48 + bottom, left: 48, right: 48 }, maxZoom: 15, duration: 0 });
        }
      } else if (follow?.length) {
        const f = markers.find((k) => follow.includes(k.id));
        // Out of sight, or under the panel laid over the map: brought back into the clear part.
        const at = f ? m.project([f.lng, f.lat]) : null;
        const box = m.getContainer();
        if (f && at && (at.x < 0 || at.x > box.clientWidth || at.y < top || at.y > box.clientHeight - bottom)) m.easeTo({ center: [f.lng, f.lat] });
      }
    });
  }, [markers, ready, follow, camera, inset?.top, inset?.bottom]);

  // The route line, drawn under the markers; replaced whenever new directions arrive.
  useEffect(() => {
    const m = map.current;
    if (!m || !ready) return;
    const data = { type: "Feature" as const, properties: {}, geometry: { type: "LineString" as const, coordinates: line ?? [] } };
    const src = m.getSource("pmu-line") as import("maplibre-gl").GeoJSONSource | undefined;
    if (src) { src.setData(data); return; }
    if (!line) return;
    m.addSource("pmu-line", { type: "geojson", data });
    // The way to drive: bold enough to follow at a glance, wider close in.
    const w = (n: number): import("maplibre-gl").DataDrivenPropertyValueSpecification<number> => ["interpolate", ["linear"], ["zoom"], 12, n * 0.6, 16, n, 18, n * 1.5];
    m.addLayer({ id: "pmu-line-casing", type: "line", source: "pmu-line", layout: { "line-join": "round", "line-cap": "round" }, paint: { "line-color": "#062a26", "line-width": w(15), "line-opacity": 0.9 } });
    m.addLayer({ id: "pmu-line", type: "line", source: "pmu-line", layout: { "line-join": "round", "line-cap": "round" }, paint: { "line-color": "#3fbfad", "line-width": w(10) } });
  }, [line, ready, styleGen]);

  // Several routes, each in its colour, under the markers.
  useEffect(() => {
    const m = map.current;
    if (!m || !ready) return;
    const data = { type: "FeatureCollection" as const, features: (lines ?? []).map((l) => ({ type: "Feature" as const, properties: { id: l.id, color: l.color, width: l.width ?? 5, opacity: l.opacity ?? 0.9, dash: !!l.dash }, geometry: { type: "LineString" as const, coordinates: l.coords } })) };
    const src = m.getSource("pmu-lines") as import("maplibre-gl").GeoJSONSource | undefined;
    if (src) { src.setData(data); return; }
    if (!lines?.length) return;
    m.addSource("pmu-lines", { type: "geojson", data });
    // Solid lines with a dark casing; dashed ones (the way still to go) lighter, without it.
    const solid: import("maplibre-gl").FilterSpecification = ["!=", ["get", "dash"], true];
    const dashed: import("maplibre-gl").FilterSpecification = ["==", ["get", "dash"], true];
    m.addLayer({ id: "pmu-lines-dash", type: "line", source: "pmu-lines", filter: dashed, layout: { "line-join": "round", "line-cap": "butt" }, paint: { "line-color": ["get", "color"], "line-width": ["get", "width"], "line-opacity": ["get", "opacity"], "line-dasharray": [1.4, 1.1] } });
    m.addLayer({ id: "pmu-lines-casing", type: "line", source: "pmu-lines", filter: solid, layout: { "line-join": "round", "line-cap": "round" }, paint: { "line-color": "#07302c", "line-width": ["+", ["get", "width"], 4], "line-opacity": ["*", ["get", "opacity"], 0.5] } });
    m.addLayer({ id: "pmu-lines", type: "line", source: "pmu-lines", filter: solid, layout: { "line-join": "round", "line-cap": "round" }, paint: { "line-color": ["get", "color"], "line-width": ["get", "width"], "line-opacity": ["get", "opacity"] } });
  }, [lines, ready, styleGen]);

  // Driving: heading-up, close in, tilted a little. Between two fixes the view (and the driver's
  // arrow with it) glides at an even speed over about the time the fixes came apart, so the map
  // moves like the bus instead of jumping and stopping at each fix. Turns take the short way round.
  useEffect(() => {
    const m = map.current;
    if (!m || !ready || !camera) return;
    const pad = camera.padding ?? { top: 0, bottom: 0 };
    m.setPadding({ top: pad.top, bottom: pad.bottom, left: 0, right: 0 });
    const now = performance.now();
    const to: Cam = { lng: camera.lng, lat: camera.lat, bearing: camera.bearing, zoom: camera.zoom ?? 16.5, pitch: camera.pitch ?? 45 };
    const g = glide.current;
    // From where the view is now (mid-glide, or as the person left it); a re-centre or a far jump eases over 0.6 s.
    const from: Cam = g ? camAt(g, now) : { lng: m.getCenter().lng, lat: m.getCenter().lat, bearing: m.getBearing(), zoom: m.getZoom(), pitch: m.getPitch() };
    const far = Math.abs(from.lat - to.lat) + Math.abs(from.lng - to.lng) > 0.01;
    const gap = g ? now - g.last : 0;
    const dur = !g || far || camera.seq !== lastSeq.current ? 600 : Math.min(2500, Math.max(400, gap));
    lastSeq.current = camera.seq;
    if (g) cancelAnimationFrame(g.frame);
    const step = () => {
      const cur = glide.current;
      if (!cur || !map.current) return;
      const c = camAt(cur, performance.now());
      map.current.jumpTo({ center: [c.lng, c.lat], bearing: c.bearing, zoom: c.zoom, pitch: c.pitch });
      pins.current.get("me")?.setLngLat([c.lng, c.lat]).setRotation(c.bearing);
      if (performance.now() - cur.start < cur.dur) cur.frame = requestAnimationFrame(step);
    };
    glide.current = { from, to, start: now, dur, last: now, frame: requestAnimationFrame(step) };
  }, [camera?.lng, camera?.lat, camera?.bearing, camera?.zoom, camera?.pitch, camera?.padding?.top, camera?.padding?.bottom, camera?.seq, ready]);
  // Not following (the person moved the map, or the overview): the glide stops where it is.
  useEffect(() => { if (!camera && glide.current) { cancelAnimationFrame(glide.current.frame); glide.current = null; } }, [camera]);
  useEffect(() => () => { if (glide.current) cancelAnimationFrame(glide.current.frame); }, []);

  // The whole way at once (the driver's overview): fitted flat and north-up.
  useEffect(() => {
    const m = map.current;
    if (!m || !ready || !overview?.points.length) return;
    void loadLib().then(({ maplibregl }) => {
      const b = new maplibregl.LngLatBounds();
      for (const p of overview.points) b.extend(p);
      m.fitBounds(b, { padding: overview.padding ?? 48, maxZoom: 16, bearing: 0, pitch: 0, duration: 700 });
    });
  }, [overview?.key, ready]);

  return (
    <div className="pmu-map" style={{ height }}>
      <div ref={box} className="pmu-map__canvas" role="region" aria-label={label} />
      {error && <p className="pmu-map__error">{error}</p>}
    </div>
  );
}
